/** Fixed header band height in PDF points (1/72 inch). */
export const PDF_HEADER_HEIGHT_PT = 72;

const HEADER_IMAGE_LEFT_MARGIN_PT = 36;
const HEADER_IMAGE_TOP_MARGIN_PT = 8;
const HEADER_RIGHT_PADDING_PT = 8;
const HEADER_CELL_GAP_PT = 12;
/** Right column width for product type / designation (pt). */
const HEADER_COL3_WIDTH_PT = 148;
const HEADER_COL3_MIN_WIDTH_PT = 96;
const HEADER_FONT_SIZE = 9;
const HEADER_TYPE_DESIGNATION_FONT_SIZE = 16;
const HEADER_LINE_HEIGHT = 12;

function isPngBytes(bytes) {
  return bytes.length >= 4
    && bytes[0] === 0x89
    && bytes[1] === 0x50
    && bytes[2] === 0x4e
    && bytes[3] === 0x47;
}

async function embedHeaderImage(pdfDoc, headerImageBytes) {
  if (isPngBytes(headerImageBytes)) {
    return pdfDoc.embedPng(headerImageBytes);
  }
  return pdfDoc.embedJpg(headerImageBytes);
}

/**
 * Normalize product field values (string or string[]) for the PDF header.
 * @param {*} value
 * @returns {string}
 */
export function formatPdfHeaderFieldValue(value) {
  if (value == null || value === '') {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (v == null ? '' : String(v).trim()))
      .filter((v) => v && v !== '\b')
      .join(', ');
  }
  const trimmed = String(value).trim();
  return trimmed === '\b' ? '' : trimmed;
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeHeaderLine(value) {
  const trimmed = (value || '').toString().trim();
  return trimmed || '—';
}

/**
 * @param {number} pageWidth
 * @param {{ width: number }} imgDims
 * @returns {{ col1: { x: number, width: number }, col2: { x: number, width: number }, col3: { x: number, width: number } }}
 */
function computeHeaderColumns(pageWidth, imgDims) {
  const contentLeft = HEADER_IMAGE_LEFT_MARGIN_PT;
  const contentRight = pageWidth - HEADER_RIGHT_PADDING_PT;
  const contentWidth = contentRight - contentLeft;

  const col1Width = imgDims.width;
  const col1 = {
    x: contentLeft,
    width: col1Width,
  };

  let col3Width = Math.min(HEADER_COL3_WIDTH_PT, Math.max(HEADER_COL3_MIN_WIDTH_PT, contentWidth * 0.26));
  const col3 = {
    x: contentRight - col3Width,
    width: col3Width,
  };

  let col2Width = col3.x - (col1.x + col1.width) - HEADER_CELL_GAP_PT - HEADER_CELL_GAP_PT;
  const minCol2Width = 120;
  if (col2Width < minCol2Width) {
    const deficit = minCol2Width - col2Width;
    col3Width = Math.max(HEADER_COL3_MIN_WIDTH_PT, col3Width - deficit);
    col3.x = contentRight - col3Width;
    col3.width = col3Width;
    col2Width = col3.x - (col1.x + col1.width) - HEADER_CELL_GAP_PT - HEADER_CELL_GAP_PT;
  }

  const col2 = {
    x: col1.x + col1.width + HEADER_CELL_GAP_PT,
    width: Math.max(0, col2Width),
  };

  return {
    col1, col2, col3,
  };
}

/**
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 * @param {number} params.x
 * @param {number} params.y
 * @param {number} params.maxWidth
 * @param {{ label: string, value: string }[]} params.lines
 */
function drawLabeledLines(page, {
  x,
  y,
  maxWidth,
  lines,
  font,
  fontBold,
  rgb,
  fontSize = HEADER_FONT_SIZE,
  skipLabel = false,
}) {
  let textY = y;
  lines.forEach(({ label, value }) => {
    const labelText = skipLabel ? '' : `${label}: `;
    const labelWidth = fontBold.widthOfTextAtSize(labelText, fontSize);

    let xOffset = 0;
    if (!skipLabel) {
      xOffset = labelWidth;
      page.drawText(labelText, {
        x,
        y: textY,
        size: fontSize,
        font: fontBold,
        color: rgb(0.2, 0.2, 0.2),
        maxWidth,
      });
    }
    page.drawText(value, {
      x: x + xOffset,
      y: textY,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
      maxWidth: Math.max(0, maxWidth - xOffset),
    });
    textY -= HEADER_LINE_HEIGHT;
  });
}

/**
 * Three-column header: logo | project info | product type / designation.
 *
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 */
function drawHeaderBand(page, {
  pageWidth,
  pageHeight,
  headerImage,
  headerInfo,
  font,
  fontBold,
  rgb,
}) {
  const imageMaxHeight = PDF_HEADER_HEIGHT_PT - HEADER_IMAGE_TOP_MARGIN_PT - HEADER_IMAGE_TOP_MARGIN_PT;
  const imageMaxWidth = (imageMaxHeight * 16) / 9;
  const imgDims = headerImage.scaleToFit(imageMaxWidth, imageMaxHeight);

  const { col1, col2, col3 } = computeHeaderColumns(pageWidth, imgDims);

  // Shared visual top edge for header content. In EmbedPDF the logo appears to extend
  // downward from this Y even though pdf-lib documents drawImage Y as bottom-left.
  const imgY = pageHeight + HEADER_IMAGE_TOP_MARGIN_PT;
  page.drawImage(headerImage, {
    x: col1.x,
    y: imgY,
    width: imgDims.width,
    height: imgDims.height,
  });

  const textTopY = imgY + imgDims.height - HEADER_LINE_HEIGHT;

  drawLabeledLines(page, {
    x: col2.x,
    y: textTopY,
    maxWidth: col2.width,
    lines: [
      {
        label: 'Project',
        value: normalizeHeaderLine(headerInfo.projectName),
      },
      {
        label: 'Project Number',
        value: normalizeHeaderLine(headerInfo.projectNumber),
      },
      {
        label: 'Phase',
        value: normalizeHeaderLine(headerInfo.projectPhase),
      },
      {
        label: 'Author',
        value: normalizeHeaderLine(headerInfo.workspaceName),
      },
      {
        label: 'Date',
        value: normalizeHeaderLine(headerInfo.issuanceDate),
      },
    ],
    font,
    fontBold,
    rgb,
  });

  drawLabeledLines(page, {
    x: col3.x,
    y: textTopY,
    maxWidth: col3.width,
    lines: [
      {
        label: 'Type / Designation',
        value: normalizeHeaderLine(headerInfo.typeDesignation),
      },
    ],
    font,
    fontBold,
    rgb,
    fontSize: HEADER_TYPE_DESIGNATION_FONT_SIZE,
    skipLabel: true,
  });
}

/**
 * Add header (image + project info) to every page and scale existing content below.
 *
 * Mutates the loaded document in place so page `/Annots` survive. Rebuilding pages
 * via embedPdf/drawPage would flatten content and drop viewer annotations.
 *
 * @param {Uint8Array} pdfBytes
 * @param {Uint8Array} headerImageBytes
 * @param {Object} [headerInfo]
 * @param {string} [headerInfo.projectName]
 * @param {string} [headerInfo.workspaceName]
 * @param {string} [headerInfo.issuanceDate]
 * @param {string} [headerInfo.typeDesignation]
 * @param {number} [headerHeightPt]
 * @returns {Promise<Uint8Array>}
 */
export async function addHeaderImageToAllPages(
  pdfBytes,
  headerImageBytes,
  headerInfo = {
  },
  headerHeightPt = PDF_HEADER_HEIGHT_PT,
) {
  const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  const pageCount = pages.length;
  if (pageCount === 0) {
    throw new Error('PDF has no pages');
  }

  const headerImage = await embedHeaderImage(pdfDoc, headerImageBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  pages.forEach((page, i) => {
    const { width, height } = page.getSize();
    if (height <= headerHeightPt) {
      throw new Error(`Page ${i + 1} is too small for the header band`);
    }
    const scale = (height - headerHeightPt) / height;

    page.scaleContent(scale, scale);
    page.scaleAnnotations(scale, scale);

    drawHeaderBand(page, {
      pageWidth: width,
      pageHeight: height,
      headerImage,
      headerInfo,
      font,
      fontBold,
      rgb,
    });
  });

  return new Uint8Array(await pdfDoc.save());
}
